/**
 * Stage A — what THIS technician can do on THIS ticket right now.
 *
 * Derived only from `my_timer_control_state` (the server's per-technician
 * reading). It is not a second timer state machine and it never answers the
 * global question "is the ticket running / paused / idle?" — on a ticket two
 * technicians share, each gets different actions at the same moment, and only
 * the server decides what a boundary did to the whole ticket.
 *
 * Presentation-agnostic: no DOM, no components. A phone surface, a desktop
 * card and a voice command all ask the same question here.
 */
import type { MyTimerControlState, MyTimerState } from '@/utils/ticketWorkLifecycle';

/** The technician's personal work state, in interaction terms. */
export type TimerWorkState =
  | 'checking'          // not read yet (or the read failed): offer nothing
  | 'idle'              // no work of theirs on this ticket
  | 'active'            // they are measuring work now
  | 'paused'            // their own episode is paused
  | 'awaiting_review'   // their stopped time needs allocation review
  | 'unavailable'       // canonical facts contradict each other: offer nothing
  | 'not_technician';   // no technician work identity in this shop

/** Every technician timer act, independent of button labels. */
export type TimerActionIntent = 'START' | 'PAUSE' | 'RESUME' | 'STOP' | 'END_WORK' | 'REVIEW_TIME';

const FROM_SERVER: Record<MyTimerState, TimerWorkState> = {
  UNKNOWN: 'checking',
  IDLE: 'idle',
  ACTIVE: 'active',
  PAUSED: 'paused',
  AWAITING_REVIEW: 'awaiting_review',
  INTEGRITY_ERROR: 'unavailable',
  NOT_TECHNICIAN: 'not_technician',
};

/**
 * Work identity comes from the server (`NOT_TECHNICIAN` is its answer), not
 * from whichever role the UI shows as primary — an owner or manager who is
 * also a technician gets technician controls.
 */
export function deriveTimerWorkState(control: Pick<MyTimerControlState, 'myState'>): TimerWorkState {
  return FROM_SERVER[control.myState] ?? 'checking';
}

const ACTIONS: Record<TimerWorkState, readonly TimerActionIntent[]> = {
  idle: ['START'],
  active: ['PAUSE', 'STOP'],
  paused: ['RESUME', 'END_WORK'],
  awaiting_review: ['REVIEW_TIME'],
  checking: [],
  unavailable: [],
  not_technician: [],
};

/** The primary actions this state offers, in display order. */
export function availableTimerActions(state: TimerWorkState): readonly TimerActionIntent[] {
  return ACTIONS[state];
}

/** Mutating acts (everything but opening the review) — never offered while checking/unavailable. */
export function isTimerMutation(intent: TimerActionIntent): boolean {
  return intent !== 'REVIEW_TIME';
}
